import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../utils/constants';
import { getImageUrl } from '../utils/helpers';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';

const SubjectSubCategory = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [subCategories, setSubCategories] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);

  // Get params from navigation state (matching mobile app)
  const { id, type, name, flag } = location.state || {};
  const actualId = id || subjectId;

  useEffect(() => {
    fetchSubCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualId]);

  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      console.log('Fetching subject subcategories:', { actualId, subjectId, flag });
      
      // Mobile app uses /subjectfolder/{id}
      const response = await axios.get(`${API_URL}/subjectfolder/${actualId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
      
      console.log('SubjectSubCategory Full Response:', response);
      console.log('SubjectSubCategory Response Data:', response.data);
      console.log('SubjectSubCategory Response Data Type:', typeof response.data);
      console.log('SubjectSubCategory Response Is Array:', Array.isArray(response.data));
      
      // Save raw response for debugging
      setRawResponse(response.data);
      
      if (response.status === 200 && response.data) {
        // Handle both array and object with data property
        const subCategoriesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data.data || response.data);
        
        console.log('Setting subcategories:', subCategoriesData);
        console.log('SubCategories length:', Array.isArray(subCategoriesData) ? subCategoriesData.length : 'not an array');
        setSubCategories(Array.isArray(subCategoriesData) ? subCategoriesData : []);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.message || 'Failed to load subcategories');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to Videobysubject (matching mobile app handleClick)
  const handleSubCategoryClick = useCallback((item) => {
    // Check if user has purchased (flag === 1 means NOT purchased)
    if (flag === 1) {
      toast.error('Please purchase this course to access the content');
      return;
    }
    
    navigate(`/videos/${item.id}`, {
      state: {
        id: item.id,
        type: item.type,
        name: item.video_categories,
        flag,
      }
    });
  }, [navigate, flag]);

  if (loading) return <Loader />;

  // If not purchased (flag === 1), show purchase required message
  if (flag === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-8xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Purchase Required
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Please purchase this course to access the video categories and content.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            {name || 'Select Video Category'}
          </h1>
          <p className="text-white/90">Choose a category to view content</p>
          
          {/* Debug Info */}
          
        </div>
      </div>

      {/* SubCategories Grid */}
      <div className="py-8 sm:py-12 lg:py-16">
        <div className="container">
          {subCategories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              {subCategories.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSubCategoryClick(item)}
                  className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group ${
                    flag === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {/* SubCategory Image */}
                  {item.images && (
                    <div className="relative h-48 overflow-hidden bg-gray-200">
                      <img 
                        src={getImageUrl(item.images)}
                        alt={item.video_categories}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x200?text=Category';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* SubCategory Name */}
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-gray-800 text-center line-clamp-2">
                      {item.video_categories}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-24">
              <div className="text-6xl sm:text-8xl mb-6">📁</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-3">
                No Video Categories Found
              </h3>
              <p className="text-gray-500 text-base sm:text-lg mb-8">
                No video categories available for this subject.
              </p>
              
              {/* Debug: Show raw API response */}
              {rawResponse && (
                <div className="max-w-2xl mx-auto bg-gray-100 p-4 rounded text-left mb-8">
                  <h4 className="font-bold mb-2">API Response Debug:</h4>
                  <pre className="text-xs overflow-auto max-h-96">
                    {JSON.stringify(rawResponse, null, 2)}
                  </pre>
                </div>
              )}
              
              <button 
                onClick={() => navigate(-1)} 
                className="btn btn-primary btn-large"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectSubCategory;

